/**
 * <s-cart-upsell> — "Complete the look" rows in the cart drawer.
 *
 * Server markup lists fallback picks from a collection. On connect, this element asks Shopify's
 * product recommendations for the first cart item (complementary first, then related), drops
 * products already in the cart or sold out, and swaps the list for up to [data-limit] picks.
 * If recommendations come back empty or fail, the server fallback stays.
 *
 * Each row is a native cart form (posts `id`), so adding works without JS. With JS, submit adds
 * the variant via Shopify Standard Actions; the shared updateCart override then refreshes the
 * cart drawer partials in place (which re-renders and reconnects this element).
 *
 * Element dataset:
 *   data-url            routes.product_recommendations_url (no extension)
 *   data-product-id     product to base picks on (first cart item)
 *   data-exclude        comma-separated product ids already in the cart
 *   data-limit          max rows (default 3)
 *   data-money-format   shop.money_format
 *   data-add-label / data-add-aria / data-size-aria / data-sold-out / data-sale-label / data-regular-label
 *   data-error-message
 *
 * Expected DOM: <ul data-upsell-list> … <form data-upsell-form> … </ul> <p data-upsell-error hidden>
 */
const cache = new Map();

class SCartUpsell extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    this.addEventListener('submit', (event) => this.#onSubmit(event), { signal: this.ac.signal });
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.ac?.abort();
  }

  async #fetchIntent(intent) {
    const { url, productId } = this.dataset;
    const key = `${productId}:${intent}`;
    if (!cache.has(key)) {
      const endpoint = `${url}.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
      cache.set(key, fetch(endpoint, { signal: this.ac.signal })
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((d) => d.products || [])
        .catch((e) => { cache.delete(key); throw e; }));
    }
    return cache.get(key);
  }

  async #loadRecommendations() {
    if (!this.dataset.url || !this.dataset.productId) return;
    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    const limit = parseInt(this.dataset.limit, 10) || 3;
    const pick = (products) => products.filter((p) => p.available && !exclude.has(String(p.id))).slice(0, limit);

    try {
      let picks = pick(await this.#fetchIntent('complementary'));
      if (!picks.length) picks = pick(await this.#fetchIntent('related'));
      if (!picks.length || !this.isConnected) return;
      const list = this.querySelector('[data-upsell-list]');
      if (list) list.replaceChildren(...picks.map((p) => this.#row(p)));
    } catch (error) {
      // Keep the server-rendered fallback picks.
    }
  }

  #el(tag, className, attrs = {}) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    return el;
  }

  #imageUrl(src, width) {
    if (!src) return '';
    const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  }

  #row(product) {
    const variants = product.variants || [];
    const firstAvailable = variants.find((v) => v.available) || variants[0];
    const fill = (tpl) => (tpl || '').replace('__title__', product.title);

    const li = this.#el('li', 'flex items-center gap-3');

    const media = this.#el('a', 'block shrink-0 w-14 aspect-[3/4] overflow-hidden bg-surface-secondary', { href: product.url, 'aria-hidden': 'true', tabindex: '-1' });
    const imgSrc = product.featured_image;
    if (imgSrc) {
      const img = this.#el('img', 'size-full object-cover', {
        src: this.#imageUrl(imgSrc, 112),
        srcset: `${this.#imageUrl(imgSrc, 56)} 56w, ${this.#imageUrl(imgSrc, 112)} 112w, ${this.#imageUrl(imgSrc, 168)} 168w`,
        sizes: '56px', alt: product.title, loading: 'lazy', width: '56', height: '75',
      });
      media.append(img);
    }

    const info = this.#el('div', 'min-w-0 flex-1');
    const title = this.#el('a', 'block text-sm no-underline text-on-surface hover:opacity-70 motion-safe:transition-opacity line-clamp-1', { href: product.url });
    title.textContent = product.title;
    const price = this.#el('div', 'type-body inline-flex items-baseline gap-2 flex-wrap text-xs font-medium [&_span]:text-on-surface-secondary');
    const variantPrice = firstAvailable?.price ?? product.price;
    const compare = firstAvailable?.compare_at_price;
    const current = this.#el('span', compare > variantPrice ? 'text-sale' : 'text-on-surface');
    const bdi = this.#el('bdi');
    bdi.textContent = this.#formatMoney(variantPrice);
    current.append(bdi);
    price.append(current);
    if (compare > variantPrice) {
      const s = this.#el('s', 'text-compare text-sm');
      const sb = this.#el('bdi');
      sb.textContent = this.#formatMoney(compare);
      s.append(sb);
      price.append(s);
    }
    info.append(title, price);

    const form = this.#el('form', 'flex shrink-0 items-center gap-1.5', { method: 'post', action: this.dataset.addUrl || '', 'data-upsell-form': '' });
    const single = variants.length <= 1 && (!firstAvailable || firstAvailable.title === 'Default Title');
    if (single) {
      form.append(this.#el('input', '', { type: 'hidden', name: 'id', value: String(firstAvailable?.id || '') }));
    } else {
      const select = this.#el('select', 'h-9 w-16 cursor-pointer border border-border bg-surface px-1.5 text-xs text-on-surface rounded-input', {
        name: 'id', 'aria-label': fill(this.dataset.sizeAria),
      });
      for (const v of variants) {
        const opt = this.#el('option', '', { value: String(v.id) });
        opt.textContent = v.available ? v.title : `${v.title} (${this.dataset.soldOut || ''})`;
        opt.disabled = !v.available;
        opt.selected = v.id === firstAvailable?.id;
        select.append(opt);
      }
      form.append(select);
    }
    const button = this.#el('button', 'h-9 cursor-pointer border border-on-surface bg-surface px-3 text-[0.6875rem] font-medium uppercase tracking-[0.12em] text-on-surface hover:bg-on-surface hover:text-surface motion-safe:transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent', {
      type: 'submit', 'aria-label': fill(this.dataset.addAria),
    });
    button.textContent = this.dataset.addLabel || '';
    form.append(button);

    li.append(media, info, form);
    return li;
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const group = (n, decimals, thousands, decimal) => {
      const [whole, frac] = n.toFixed(decimals).split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${decimal}${frac}` : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        default: return group(value, 2, ',', '.');
      }
    });
  }

  async #onSubmit(event) {
    const form = event.target.closest?.('[data-upsell-form]');
    if (!form || !window.Shopify?.actions?.updateCart) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;

    const variantId = String(new FormData(form).get('id') || '').trim();
    if (!variantId) return;

    const error = this.querySelector('[data-upsell-error]');
    if (error) { error.hidden = true; error.textContent = ''; }
    const button = form.querySelector('button[type="submit"]');
    form.setAttribute('aria-busy', 'true');
    button?.classList.add('opacity-50', 'cursor-wait');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'cart', detail: { source: 's-cart-upsell' } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
    } catch (err) {
      if (error && this.isConnected) {
        error.textContent = err?.message || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      form.removeAttribute('aria-busy');
      button?.classList.remove('opacity-50', 'cursor-wait');
    }
  }
}

if (!customElements.get('s-cart-upsell')) {
  customElements.define('s-cart-upsell', SCartUpsell);
}
